import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, CalendarClock } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const EMPTY = { name: "", email: "", phone: "", category: "maintenance", address: "", notes: "", time: "10:00", status: "confirme" };

function fmtDate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function displayFR(d) {
  if (!d) return "Choisir une date";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function AppointmentCreateModal({ open, onOpenChange, initialDate, onCreated }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [date, setDate] = useState(initialDate || null);
  const [openCal, setOpenCal] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !date || !form.time) {
      toast.error("Nom, email, date et heure requis");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/appointments", {
        ...form,
        date: fmtDate(date),
      });
      toast.success("Rendez-vous ajouté");
      setForm({ ...EMPTY }); setDate(null);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0d0d0d] text-white">
        <DialogHeader>
          <DialogTitle className="font-head text-xl uppercase text-white flex items-center gap-2"><CalendarClock size={18} className="text-[#ff4d4d]" />Nouvel événement</DialogTitle>
          <DialogDescription className="text-white/60">Ajoutez un rendez-vous ou un événement à votre calendrier.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-appt-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Nom *</Label>
              <Input value={form.name} onChange={set("name")} className="border-white/10 bg-black/50" data-testid="admin-appt-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Email *</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="border-white/10 bg-black/50" data-testid="admin-appt-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Téléphone</Label>
              <Input value={form.phone} onChange={set("phone")} className="border-white/10 bg-black/50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="border-white/10 bg-black/50 text-white" data-testid="admin-appt-category"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#111] text-white">
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="evenementiel">Événementiel</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Date *</Label>
              <Popover open={openCal} onOpenChange={setOpenCal}>
                <PopoverTrigger asChild>
                  <button type="button" className="flex w-full items-center justify-between rounded-md border border-white/10 bg-black/50 px-3 py-2 text-left text-sm text-white" data-testid="admin-appt-date">
                    <span className={date ? "text-white" : "text-white/40"}>{displayFR(date)}</span>
                    <ChevronDown size={14} className="text-white/50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-white/10 bg-[#111] p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setOpenCal(false); }} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Heure *</Label>
              <Input type="time" value={form.time} onChange={set("time")} className="border-white/10 bg-black/50" data-testid="admin-appt-time" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-white/70">Adresse</Label>
              <Input value={form.address} onChange={set("address")} className="border-white/10 bg-black/50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="border-white/10 bg-black/50 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#111] text-white">
                  <SelectItem value="confirme">Confirmé</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/70">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={set("notes")} className="border-white/10 bg-black/50" />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-white/20 px-5 py-2.5 text-sm text-white hover:bg-white/5">Annuler</button>
            <button type="submit" disabled={busy} className="rounded-full bg-brand-gradient px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="admin-appt-submit">
              {busy ? "Enregistrement…" : "Ajouter au calendrier"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
